// Payment log (V0.4.3): payment instructions from Summation Partners to the client's accounts team — read by Claude
// from an uploaded email / PDF / screenshot / pasted text (and from V0.5 straight from the pm@ inbox). Each line gets
// a suggested claim or variation; exact amounts are one click. Assigning a line marks the claim paid (claims in full)
// or adds a (part) payment to a variation. Nothing changes until a PM assigns. SWI staff only.
import { useMemo, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router';
import { useApp } from '../lib/app';
import { supabase } from '../lib/supabase';
import { useRows } from '../lib/useRows';
import { api, downloadFile, write } from '../lib/api';
import { fmtDate, fmtDateTime, fmtMoney } from '../lib/format';
import { Button, Card, Empty, PageHeader, errText, useToast } from '../ui/kit';

const NONE = '00000000-0000-0000-0000-000000000000';
const INTERNAL_ROLES = new Set(['super_admin', 'pm', 'field', 'swi_finance']);

type Instr = { id: string; source: string; subject: string | null; sender: string | null; sent_on: string | null; received_at: string | null; status: string; error: string | null; total: number | null; notes: string | null; file_id: string | null; created_at: string };
type Line = {
  id: string;
  instruction_id: string;
  line_no: number;
  payee_raw: string | null;
  ref: string | null;
  amount: number;
  match_kind: 'exact' | 'near' | 'part' | 'none';
  suggested_item_id: string | null;
  suggest_note: string | null;
  item_id: string | null;
  assigned_kind: string | null;
  assigned_at: string | null;
  not_ours: boolean;
};
type It = { id: string; type: 'claim' | 'variation'; number: string | null; ref_no: string | null; title: string; amount: number | null; status: string; paid_total: number | null; building_ids: string[] };

const money = (n: number | null | undefined) => fmtMoney(n).replace('FJD ', '');
const short = (n: string | null) => (n ? n.replace(/^TavPhase2-/, '') : '—');
const TAG: Record<Line['match_kind'], string> = { exact: 'Exact match', near: 'Close match', part: 'Part payment', none: 'No match' };

export default function Payments() {
  const { siteId, can, access } = useApp();
  const toast = useToast();
  const internal = Boolean(access?.super_admin || access?.roles.some((r) => INTERNAL_ROLES.has(r.role)));
  const editable = can('claims', 4) || can('variations', 4);
  const [tab, setTab] = useState<'todo' | 'all' | 'mail' | 'notours'>('todo');
  const [busy, setBusy] = useState<string | null>(null);
  const [mode, setMode] = useState<null | 'paste' | 'hand'>(null);
  const [over, setOver] = useState(false);
  const fileIn = useRef<HTMLInputElement>(null);

  const instrs = useRows<Instr>(
    () => supabase.from('payment_instructions').select('id, source, subject, sender, sent_on, received_at, status, error, total, notes, file_id, created_at').eq('site_id', siteId ?? NONE).is('deleted_at', null).order('created_at', { ascending: false }).limit(300),
    [siteId],
    ['payment_instructions'],
  );
  const lines = useRows<Line>(
    () => supabase.from('payment_lines').select('id, instruction_id, line_no, payee_raw, ref, amount, match_kind, suggested_item_id, suggest_note, item_id, assigned_kind, assigned_at, not_ours').eq('site_id', siteId ?? NONE).is('deleted_at', null).order('line_no').limit(5000),
    [siteId],
    ['payment_lines'],
  );
  const items = useRows<It>(
    () => supabase.from('items').select('id, type, number, ref_no, title, amount, status, paid_total, building_ids').eq('site_id', siteId ?? NONE).in('type', ['claim', 'variation']).is('deleted_at', null).limit(10000),
    [siteId],
    ['items'],
  );
  const iMap = useMemo(() => new Map(items.rows.map((i) => [i.id, i])), [items.rows]);
  const byInstr = useMemo(() => {
    const m = new Map<string, Line[]>();
    for (const l of lines.rows) m.set(l.instruction_id, [...(m.get(l.instruction_id) ?? []), l]);
    return m;
  }, [lines.rows]);

  if (!can('claims') && !can('variations')) return <Navigate to="/" replace />;
  if (!internal) return <Empty>The payment log is for SWI staff.</Empty>;

  const todo = lines.rows.filter((l) => !l.item_id && !l.not_ours);
  const exactN = todo.filter((l) => l.match_kind === 'exact' && l.suggested_item_id).length;
  const month = new Date().toISOString().slice(0, 7);
  const assignedMonth = lines.rows.filter((l) => l.item_id && (l.assigned_at ?? '').slice(0, 7) === month);
  const sum = (ls: Line[]) => ls.reduce((t, l) => t + Number(l.amount), 0);
  const last = instrs.rows.find((i) => i.status !== 'reading');

  const run = async (key: string, fn: () => Promise<unknown>, ok?: string) => {
    setBusy(key);
    try {
      await fn();
      if (ok) toast({ tone: 'ok', text: ok });
      void lines.reload();
      void instrs.reload();
      void items.reload();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(null);
    }
  };
  const assign = (l: Line, itemId: string, kind: 'full' | 'part') =>
    run(l.id, () => api('payment-assign', { line_id: l.id, item_id: itemId, kind }), `Assigned to ${short(iMap.get(itemId)?.number ?? null)}${kind === 'part' ? ' as a part payment' : ' · paid'}`);
  const unassign = (l: Line) => run(l.id, () => api('payment-assign', { line_id: l.id, unassign: true }), 'Assignment undone');
  const notOurs = (l: Line, v: boolean) => run(l.id, () => write('payment_lines', 'update', { id: l.id, values: { not_ours: v }, summary: v ? 'Payment line marked not ours' : 'Payment line back in the log' }));

  const upload = async (f: File) => {
    if (!siteId) return;
    await run('upload', async () => {
      const up = await api<{ bucket: string; path: string; token: string }>('upload-url', { site_id: siteId, bucket: 'documents', filename: f.name });
      const { error } = await supabase.storage.from(up.bucket).uploadToSignedUrl(up.path, up.token, f, { contentType: f.type || 'application/octet-stream' });
      if (error) throw new Error(error.message);
      await api('payment-read', { site_id: siteId, file: { bucket: up.bucket, path: up.path, name: f.name, mime: f.type || null, size: f.size } });
    }, `Claude is reading ${f.name} — the lines appear here in a minute`);
  };

  const shownInstr = instrs.rows.filter((i) => {
    const ls = byInstr.get(i.id) ?? [];
    if (tab === 'todo') return i.status === 'reading' || i.status === 'failed' || ls.some((l) => !l.item_id && !l.not_ours);
    if (tab === 'notours') return ls.some((l) => l.not_ours);
    return true;
  });

  return (
    <div className="pay-page">
      <PageHeader
        title="Payment log"
        sub={<>Payment instructions from Summation Partners, matched to claims and variations. Nothing changes on a claim until a PM assigns the line.</>}
        actions={
          editable ? (
            <>
              <Button kind="primary" onClick={() => fileIn.current?.click()} disabled={busy === 'upload'}>
                {busy === 'upload' ? 'Uploading…' : 'Upload email for Claude'}
              </Button>
              <Button onClick={() => setMode(mode === 'paste' ? null : 'paste')}>Paste email text</Button>
              <Button kind="ghost" onClick={() => setMode(mode === 'hand' ? null : 'hand')}>
                + Add by hand
              </Button>
            </>
          ) : null
        }
      />

      <section className="pay-kpis" aria-label="Summary">
        <div className={`pk todo ${todo.length ? 'attn' : ''}`}>
          <span className="lbl">
            {todo.length ? <span className="pdot" aria-hidden="true" /> : null}To assign
          </span>
          <b>{todo.length} lines</b>
          <span className="sub">FJD {money(sum(todo))}</span>
        </div>
        <div className="pk exact">
          <span className="lbl">Exact match ready</span>
          <b>{exactN}</b>
          <span className="sub">one click each</span>
        </div>
        <div className="pk">
          <span className="lbl">Assigned this month</span>
          <b>{assignedMonth.length}</b>
          <span className="sub">FJD {money(sum(assignedMonth))}</span>
        </div>
        <div className="pk">
          <span className="lbl">Last instruction</span>
          <b className="small-b">{last ? fmtDate(last.sent_on ?? last.created_at) : '—'}</b>
          <span className="sub">pm@ inbox reading starts with V0.5</span>
        </div>
      </section>

      {editable ? (
        <label
          className={`pay-drop ${over ? 'over' : ''}`}
          onDragOver={(e) => (e.preventDefault(), setOver(true))}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            const f = e.dataTransfer.files?.[0];
            if (f) void upload(f);
          }}
        >
          <input
            ref={fileIn}
            type="file"
            className="sr"
            accept=".eml,.msg,.pdf,.png,.jpg,.jpeg,.webp,.txt,message/rfc822,application/vnd.ms-outlook,application/pdf,image/*"
            aria-label="Upload a payment email"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void upload(f);
            }}
          />
          <span className="pd-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 16V4M7 9l5-5 5 5" />
              <path d="M4 16v4h16v-4" />
            </svg>
          </span>
          <span className="pd-text">
            <b>Drop an email here, or click to upload</b> (.eml, .msg, PDF or a screenshot)
            <span className="sub">For instructions that didn't reach pm@ — forwarded, sent on WhatsApp, or read out in a meeting. Claude reads it and adds the lines below with suggested matches.</span>
          </span>
        </label>
      ) : null}

      {mode === 'paste' ? <PasteForm siteId={siteId} busy={busy === 'paste'} onDone={() => setMode(null)} run={run} /> : null}
      {mode === 'hand' ? <HandForm siteId={siteId} busy={busy === 'hand'} onDone={() => setMode(null)} run={run} /> : null}

      <div className="pay-tabs" role="tablist" aria-label="Payment log views">
        {(
          [
            ['todo', `To assign · ${todo.length}`],
            ['all', 'All lines'],
            ['mail', 'By email'],
            ['notours', 'Not ours'],
          ] as const
        ).map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>
            {l}
          </button>
        ))}
      </div>

      {instrs.loading ? (
        <Empty>Loading…</Empty>
      ) : shownInstr.length === 0 ? (
        <Empty>{tab === 'todo' ? 'Nothing to assign — all payment lines are matched.' : 'No payment instructions yet. Upload one above.'}</Empty>
      ) : (
        shownInstr.map((i) => {
          const ls = (byInstr.get(i.id) ?? []).filter((l) => (tab === 'todo' ? !l.item_id && !l.not_ours : tab === 'notours' ? l.not_ours : true));
          const all = byInstr.get(i.id) ?? [];
          return (
            <Card key={i.id} pad={false}>
              <div className={`pay-head ${i.status}`}>
                <span className="src">{i.source === 'manual' ? 'By hand' : i.source === 'paste' ? 'Pasted' : i.source === 'email' ? 'pm@' : 'Upload'}</span>
                <span className="ttl">
                  <b>{i.subject || 'Payment instruction'}</b>
                  <span className="sub">
                    {[i.sender, i.sent_on ? `sent ${fmtDate(i.sent_on)}` : null, `added ${fmtDateTime(i.created_at)}`, `${all.length} line${all.length === 1 ? '' : 's'}`, i.total ? `FJD ${money(i.total)}` : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                {i.status === 'reading' ? <span className="reading">Claude is reading…</span> : null}
                {i.status === 'failed' ? <span className="failed">Couldn't read: {i.error ?? 'unknown error'}</span> : null}
                {i.status === 'not_payment' ? <span className="failed">Not a payment instruction</span> : null}
                {i.file_id ? (
                  <button type="button" className="linkish small" onClick={() => void downloadFile(i.file_id!).catch((e) => toast({ tone: 'error', text: errText(e) }))}>
                    Open file
                  </button>
                ) : null}
              </div>
              {i.notes ? <div className="pay-note">Claude's note: {i.notes}</div> : null}
              {tab === 'mail' && !ls.length ? null : ls.length ? (
                <div className="table-wrap">
                  <table className="t pay-t">
                    <thead>
                      <tr>
                        <th>Payee</th>
                        <th>Their reference</th>
                        <th className="right">Amount</th>
                        <th>Suggested match</th>
                        <th className="c-act">Assign</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ls.map((l) => (
                        <LineRow key={l.id} l={l} iMap={iMap} items={items.rows} editable={editable} busy={busy === l.id} assign={assign} unassign={unassign} notOurs={notOurs} />
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </Card>
          );
        })
      )}
    </div>
  );
}

function LineRow({
  l,
  iMap,
  items,
  editable,
  busy,
  assign,
  unassign,
  notOurs,
}: {
  l: Line;
  iMap: Map<string, It>;
  items: It[];
  editable: boolean;
  busy: boolean;
  assign: (l: Line, item: string, kind: 'full' | 'part') => void;
  unassign: (l: Line) => void;
  notOurs: (l: Line, v: boolean) => void;
}) {
  const [picking, setPicking] = useState(false);
  const sug = l.suggested_item_id ? iMap.get(l.suggested_item_id) : undefined;
  const done = l.item_id ? iMap.get(l.item_id) : undefined;
  const link = (it: It) => <Link to={`/r/${it.type === 'claim' ? 'claims' : 'variations'}/${it.id}`}>{short(it.number)}</Link>;
  return (
    <tr className={l.item_id ? 'pl-done' : l.not_ours ? 'pl-notours' : l.match_kind === 'none' ? 'pl-none' : ''}>
      <td>
        <b>{l.payee_raw ?? '—'}</b>
        <div className="sub-line">line {l.line_no}</div>
      </td>
      <td>{l.ref ?? '—'}</td>
      <td className="right">
        <b>{money(l.amount)}</b>
      </td>
      <td>
        <span className={`mtag m-${l.match_kind}`}>{TAG[l.match_kind]}</span>
        {sug ? (
          <div className="sug">
            {link(sug)} · {sug.ref_no ?? sug.title} · FJD {money(sug.amount)} {sug.type === 'variation' ? 'VEP' : 'VIP'}
          </div>
        ) : null}
        {l.suggest_note ? <div className="sub-line">{l.suggest_note}</div> : null}
      </td>
      <td className="c-act">
        {l.item_id ? (
          <>
            <span className="assigned">
              ✓ {l.assigned_kind === 'part' ? 'Part payment on ' : 'Assigned to '}
              {done ? link(done) : 'item'}
              {l.assigned_kind === 'part' ? '' : ' · paid'}
            </span>
            {editable ? (
              <div>
                <button type="button" className="linkish small" disabled={busy} onClick={() => unassign(l)}>
                  Undo
                </button>
              </div>
            ) : null}
          </>
        ) : l.not_ours ? (
          editable ? (
            <button type="button" className="linkish small" disabled={busy} onClick={() => notOurs(l, false)}>
              Put back in the log
            </button>
          ) : (
            'Not ours'
          )
        ) : !editable ? null : picking ? (
          <Picker items={items} amount={l.amount} busy={busy} onPick={(id, kind) => (assign(l, id, kind), setPicking(false))} onCancel={() => setPicking(false)} />
        ) : (
          <div className="pl-acts">
            {sug && l.match_kind === 'exact' ? (
              <button type="button" className="btn btn-primary pay-go" disabled={busy} onClick={() => assign(l, sug.id, 'full')}>
                ✓ Assign to {short(sug.number)}
              </button>
            ) : null}
            {sug && l.match_kind === 'part' ? (
              <button type="button" className="btn btn-secondary pay-part" disabled={busy} onClick={() => assign(l, sug.id, 'part')}>
                Assign as part payment
              </button>
            ) : null}
            {sug && l.match_kind === 'near' ? (
              <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => assign(l, sug.id, sug.type === 'variation' && l.amount < Number(sug.amount ?? 0) * 0.98 ? 'part' : 'full')}>
                Assign to {short(sug.number)}
              </button>
            ) : null}
            <button type="button" className="btn btn-ghost" onClick={() => setPicking(true)}>
              {sug ? 'Pick another…' : 'Pick claim / variation…'}
            </button>
            <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => notOurs(l, true)}>
              Not ours
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

function Picker({ items, amount, busy, onPick, onCancel }: { items: It[]; amount: number; busy: boolean; onPick: (id: string, kind: 'full' | 'part') => void; onCancel: () => void }) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState('');
  const [kind, setKind] = useState<'full' | 'part'>('full');
  const open = items.filter((i) => (i.type === 'claim' ? !['paid', 'rejected', 'void'].includes(i.status) : !['rejected', 'void'].includes(i.status)));
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const list = open
    .filter((i) => words.every((w) => [i.number, i.ref_no, i.title, String(i.amount ?? '')].join(' ').toLowerCase().includes(w)))
    .sort((a, b) => Math.abs(Number(a.amount ?? 0) - amount) - Math.abs(Number(b.amount ?? 0) - amount))
    .slice(0, 50);
  const chosen = open.find((i) => i.id === sel);
  return (
    <div className="picker">
      <input type="search" autoFocus placeholder="Find by number, ref, topic…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find claim or variation" />
      <select size={5} value={sel} onChange={(e) => setSel(e.target.value)} aria-label="Claim or variation">
        {list.map((i) => (
          <option key={i.id} value={i.id}>
            {short(i.number)} · {i.ref_no ?? i.title.slice(0, 40)} · {money(i.amount)}
          </option>
        ))}
      </select>
      {chosen?.type === 'variation' ? (
        <div className="pk-kind" role="radiogroup" aria-label="Payment kind">
          <label>
            <input type="radio" checked={kind === 'full'} onChange={() => setKind('full')} /> Full
          </label>
          <label>
            <input type="radio" checked={kind === 'part'} onChange={() => setKind('part')} /> Part payment
          </label>
        </div>
      ) : null}
      <div className="pl-acts">
        <button type="button" className="btn btn-primary" disabled={!sel || busy} onClick={() => onPick(sel, chosen?.type === 'variation' ? kind : 'full')}>
          Assign
        </button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function PasteForm({ siteId, busy, onDone, run }: { siteId: string | null; busy: boolean; onDone: () => void; run: (k: string, fn: () => Promise<unknown>, ok?: string) => Promise<void> }) {
  const [subject, setSubject] = useState('');
  const [text, setText] = useState('');
  return (
    <Card title="Paste a payment email">
      <div className="pay-form">
        <label className="field">
          <span className="field-label">Subject (optional)</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} />
        </label>
        <label className="field wide">
          <span className="field-label">Email text</span>
          <textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste the whole email, including the list of payments" />
        </label>
        <div className="pl-acts">
          <Button kind="primary" disabled={!text.trim() || busy} onClick={() => void run('paste', () => api('payment-read', { site_id: siteId, text, subject }), 'Claude is reading the pasted email').then(onDone)}>
            Read with Claude
          </Button>
          <Button kind="ghost" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </div>
    </Card>
  );
}

function HandForm({ siteId, busy, onDone, run }: { siteId: string | null; busy: boolean; onDone: () => void; run: (k: string, fn: () => Promise<unknown>, ok?: string) => Promise<void> }) {
  const [payee, setPayee] = useState('');
  const [ref, setRef] = useState('');
  const [amount, setAmount] = useState('');
  const [sent, setSent] = useState(new Date().toISOString().slice(0, 10));
  return (
    <Card title="Add a payment by hand">
      <div className="pay-form grid">
        <label className="field">
          <span className="field-label">Payee</span>
          <input value={payee} onChange={(e) => setPayee(e.target.value)} placeholder="e.g. PBS" />
        </label>
        <label className="field">
          <span className="field-label">Reference</span>
          <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. Claim 18 Treehouse" />
        </label>
        <label className="field">
          <span className="field-label">Amount (FJD)</span>
          <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </label>
        <label className="field">
          <span className="field-label">Instruction date</span>
          <input type="date" value={sent} onChange={(e) => setSent(e.target.value)} />
        </label>
        <div className="pl-acts wide">
          <Button
            kind="primary"
            disabled={!(Number(amount) > 0) || busy}
            onClick={() => void run('hand', () => api('payment-read', { site_id: siteId, manual: { payee, ref, amount: Number(amount), sent_on: sent } }), 'Payment added — pick what it pays below').then(onDone)}
          >
            Add to the log
          </Button>
          <Button kind="ghost" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </div>
    </Card>
  );
}
